import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { useOptionalThemeContext } from './themes/DynamicThemeContext';
import './ReflectionCompletenessBar.css';

interface Props {
  completeness: number | null;
  evaluating: boolean;
  outcome: 'criteria_met' | 'limit_reached' | 'skipped' | 'unavailable' | null;
}

/** The number and fill share one clock. Mounting saved evidence never replays a fill. */
export const ReflectionCompletenessBar: React.FC<Props> = ({ completeness, evaluating, outcome }) => {
  const theme = useOptionalThemeContext();
  const [systemReducedMotion, setSystemReducedMotion] = useState(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setSystemReducedMotion(query.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  const reducedMotion = Boolean(theme?.preferences.reducedMotion || systemReducedMotion);
  const target = typeof completeness === 'number' && Number.isInteger(completeness)
    && completeness >= 0 && completeness <= 100 ? completeness : null;
  const [value, setValue] = useState(target);
  const [animating, setAnimating] = useState(false);
  const current = useRef(target);
  useEffect(() => {
    const origin = current.current;
    if (target == null || origin == null || reducedMotion || origin === target) {
      current.current = target;
      setValue(target);
      setAnimating(false);
      return undefined;
    }
    const start = performance.now();
    const duration = Math.min(650 + Math.abs(target - origin) * 12, 1500);
    let frame = 0;
    setAnimating(true);
    const tick = (now: number) => {
      const progress = Math.max(0, Math.min((now - start) / duration, 1));
      const eased = 1 - (1 - progress) ** 3;
      current.current = origin + (target - origin) * eased;
      setValue(current.current);
      if (progress < 1) frame = requestAnimationFrame(tick);
      else setAnimating(false);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, reducedMotion]);

  const complete = outcome === 'criteria_met';
  const saved = outcome === 'limit_reached';
  // Unknown historical scores remain unknown, even for completed reflections.
  const displayed = target == null ? null : reducedMotion ? target : value ?? target;
  const label = evaluating ? 'Reading your answer…' : complete ? 'Complete' : saved ? 'Saved' : 'Reflection';
  return <div className="reflection-completeness" data-reduced-motion={reducedMotion || undefined}>
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span aria-hidden="true" className="inline-flex items-center gap-1 font-semibold tabular-nums text-foreground">
        {(complete || saved) && <Check className="h-3.5 w-3.5 text-emerald-700" />}
        {displayed == null ? 'Completeness not available' : `${Math.round(displayed)}%`}
      </span>
    </div>
    {target == null ? <p className="sr-only">Completeness not available for this saved reflection.</p> : <div
      role="progressbar" aria-label="Reflection completeness" aria-valuemin={0} aria-valuemax={100} aria-valuenow={target}
      aria-valuetext={`${complete ? 'Complete' : saved ? 'Saved' : 'Reflection'}, ${target}% complete`}
      aria-busy={evaluating} data-animating={animating && !reducedMotion ? 'true' : 'false'}
      className={`reflection-completeness-track${evaluating ? ' reflection-completeness-waiting' : ''}`}>
      {animating && !reducedMotion && <span aria-hidden="true" className="reflection-completeness-preview" style={{ width: `${target}%` }} />}
      <span aria-hidden="true" data-testid="reflection-completeness-fill"
        className={`reflection-completeness-fill${complete ? ' reflection-completeness-complete' : ''}`}
        style={{ width: `${displayed}%` }} />
    </div>}
  </div>;
};
